import * as React from 'react'
import toast from 'react-hot-toast'
import { IDevice } from '../../../interfaces/types.ts'
import { sendCommand } from '../../../helpers/commandHelper.ts'
import { AsyncButton } from '../AsyncButton/AsyncButton.tsx'
import { PopupPortal } from '../Popup/PopupPortal.tsx'
import dataStore from '../../../store/dataStore.ts'

interface Props {
    tracker: IDevice
    open: boolean
    setOpen: (o: boolean) => void
    disabled?: boolean
}

export const AdvancedSettings: React.FC<Props> = ({ tracker, open, setOpen, disabled }) => {
    const pets = dataStore(s => s.pets)
    const [petName, setPetName] = React.useState('')

    const [bleValue, setBleValue] = React.useState('')
    const [bleError, setBleError] = React.useState('')

    const [ncsValue, setNcsValue] = React.useState('')
    const [ncsError, setNcsError] = React.useState('')

    const [mtValue, setMtValue] = React.useState('')
    const [mtError, setMtError] = React.useState('')

    React.useEffect(() => {
        if (!open) return
        const pet = pets.find(p => p._id === tracker.petId)
        setPetName(pet?.petName || '')
        setBleValue(String(tracker.status.powerSavingsScanRate ?? ''))
        setNcsValue(String(tracker.status.noCellularScanRate ?? ''))
        setMtValue(String(tracker.status.mt ?? ''))
        setBleError('')
        setNcsError('')
        setMtError('')
    }, [open])

    const sendBle = async () => {
        const n = parseInt(bleValue, 10)
        if (isNaN(n) || n <= 0) { setBleError('Must be a positive number of minutes'); return }
        setBleError('')
        const ok = await sendCommand('powerSaveRate', tracker.iccid, n)
        if (ok) toast.success('BLE scan interval saved')
        else toast.error('BLE scan command failed. Try again later.')
    }

    const sendNcs = async () => {
        const n = parseInt(ncsValue, 10)
        if (isNaN(n) || n <= 0) { setNcsError('Must be a positive number of minutes'); return }
        setNcsError('')
        const ok = await sendCommand('noCellularRate', tracker.iccid, n)
        if (ok) toast.success('NCS timer saved')
        else toast.error('NCS command failed. Try again later.')
    }

    const sendMt = async () => {
        const n = parseInt(mtValue, 10)
        if (isNaN(n) || n < 0 || n > 30) { setMtError('Must be between 0 and 30'); return }
        setMtError('')
        const ok = await sendCommand('motionThreshold', tracker.iccid, n)
        if (ok) toast.success('Motion threshold saved')
        else toast.error('Motion threshold command failed. Try again later.')
    }

    const row = (
        label: string,
        hint: string,
        value: string,
        error: string,
        onChange: (v: string) => void,
        onSend: () => Promise<void>
    ) => (
        <div className={'flex flex-col gap-1'}>
            <label className={'text-sm font-semibold'}>{label}</label>
            <div className={'flex gap-2 items-center'}>
                <div className={'relative flex-1'}>
                    <input
                        type={'number'}
                        className={`input input-sm input-bordered w-full pr-16 ${error ? 'input-error' : ''}`}
                        value={value}
                        disabled={disabled}
                        onChange={e => { onChange(e.target.value); }}
                    />
                    <span className={'absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none'}>
                        {hint}
                    </span>
                </div>
                <AsyncButton
                    classNames={'btn btn-outlined btn-secondary btn-sm whitespace-nowrap'}
                    onClick={onSend}
                    disabled={disabled}
                >
                    Send
                </AsyncButton>
            </div>
            {error
                ? <span className={'text-error text-xs'}>{error}</span>
                : <span className={'text-xs invisible'}>placeholder</span>
            }
        </div>
    )

    const panel = (
        <div className={'flex flex-col gap-4 w-full p-6 max-w-[22rem]'}>
            <div className={'prose flex flex-col items-center gap-0'}>
                <h2 className={'mt-0 mb-0 text-primary'}>Advanced Settings</h2>
                <div className={'text-md font-semibold text-primary'}>{petName}</div>
            </div>

            <div className={'flex flex-col items-start gap-2 bg-warning/20 border border-warning rounded-lg p-3 text-sm'}>
                <div className={'bg-warning font-bold shrink-0'}>⚠ Warning</div>
                <div>Changing these settings will affect the performance and behavior of your Gibi Tracker. Only change these settings under the guidance of Gibi Support.</div>
            </div>

            <div className={'flex flex-col gap-2 w-full max-w-xs mx-auto'}>
                {row('BLE Scan Interval', 'minutes', bleValue, bleError, v => { setBleValue(v); setBleError('') }, sendBle)}
                {row('NCS Timer', 'minutes', ncsValue, ncsError, v => { setNcsValue(v); setNcsError('') }, sendNcs)}
                {row('Motion Threshold', '0 – 30', mtValue, mtError, v => { setMtValue(v); setMtError('') }, sendMt)}
            </div>
        </div>
    )

    const buttons = (
        <div className={'px-6 pb-6 flex justify-start w-full'}>
            <button className={'btn btn-secondary btn-ghost text-secondary btn-sm'} onClick={() => setOpen(false)}>
                Close
            </button>
        </div>
    )

    return (
        <PopupPortal open={open}>
            {panel}
            {buttons}
        </PopupPortal>
    )
}
